import { useState, useEffect } from "react";
import { Button as AntdButton, Tag } from "antd";
import { RoadmapGrid } from "./RoadmapGrid";
import type { RoadmapVersion, AllBuildingBlocks } from "../types";
import { Clock, ChevronLeft, Pencil, Check, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

interface VersionHistoryPanelProps {
  sessionId: string;
  companyName: string;
  currentBlocks: AllBuildingBlocks;
  lastPublishedAt?: string | null;
}

function formatDateTime(dateStr: string | Date): string {
  const d = new Date(dateStr);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${date} · ${time}`;
}


function VersionLabel({
  version,
  onSave,
}: {
  version: RoadmapVersion;
  onSave: (id: string, label: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(version.label || "");

  const commit = () => {
    onSave(version.id, draft);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(version.label || "");
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 flex-1 min-w-0">
        <input
          autoFocus
          className="flex-1 min-w-0 text-xs border rounded px-1.5 py-0.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") cancel();
          }}
          placeholder="Add label..."
          maxLength={60}
        />
        <button onClick={commit} className="shrink-0 p-0.5 rounded text-emerald-600 hover:text-emerald-700">
          <Check className="h-3 w-3" />
        </button>
        <button onClick={cancel} className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-foreground">
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <button
      className="flex items-center gap-1 text-left group min-w-0"
      onClick={() => setEditing(true)}
      title="Click to edit label"
    >
      {version.label ? (
        <span className="text-xs font-medium truncate">{version.label}</span>
      ) : (
        <span className="text-xs text-muted-foreground/70 truncate">{formatDateTime(version.createdAt)}</span>
      )}
      <Pencil className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  );
}

export function VersionHistoryPanel({ sessionId, companyName, lastPublishedAt }: VersionHistoryPanelProps) {
  const [versions, setVersions] = useState<RoadmapVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewingVersion, setViewingVersion] = useState<RoadmapVersion | null>(null);

  useEffect(() => {
    const fetchVersions = async () => {
      setIsLoading(true);
      try {
        const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/versions`, {
          credentials: "include",
        });
        if (r.ok) {
          const data = await r.json();
          setVersions(data);
        }
      } catch (error) {
        console.error("Failed to fetch versions:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchVersions();
  }, [sessionId, lastPublishedAt]);

  const handleSaveLabel = async (versionId: string, label: string) => {
    await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/versions/${versionId}/label`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label }),
      credentials: "include",
    });
    setVersions((prev) =>
      prev.map((v) => (v.id === versionId ? { ...v, label } : v)),
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        Loading version history...
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-3">
        <Clock className="h-8 w-8 text-muted-foreground/40" />
        <div>
          <p className="text-sm font-medium">No published versions yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Publish draft" to save your first version snapshot.
          </p>
        </div>
      </div>
    );
  }

  if (viewingVersion) {
    return (
      <div className="flex flex-col h-full">
        <div className="shrink-0 px-3 py-2 border-b bg-amber-50 flex items-center gap-2">
          <button
            onClick={() => setViewingVersion(null)}
            className="p-0.5 rounded hover:bg-amber-100 text-amber-700"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-amber-700">
              Viewing v{viewingVersion.version} — {formatDateTime(viewingVersion.createdAt)}
              {viewingVersion.label && ` · ${viewingVersion.label}`}
            </p>
            <p className="text-[10px] text-amber-600/70">Read-only snapshot</p>
          </div>
          <AntdButton
            type="text"
            size="small"
            className="text-[10px] h-6 px-2 text-amber-700 hover:bg-amber-100"
            onClick={() => setViewingVersion(null)}
          >
            Back to live
          </AntdButton>
        </div>
        <div className="flex-1 overflow-y-auto">
          <RoadmapGrid
            buildingBlocks={viewingVersion.buildingBlocks as AllBuildingBlocks}
            companyName={companyName}
            sessionId={sessionId}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      <div className="mb-1">
        <h3 className="text-sm font-semibold">Published Versions</h3>
        <p className="text-xs text-muted-foreground">
          {versions.length} snapshot{versions.length !== 1 ? "s" : ""} · click to view
        </p>
      </div>

      <AnimatePresence initial={false}>
        {versions.map((v, idx) => (
          <motion.div
            key={v.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04 }}
          >
            <div
              className="group border rounded-lg p-3 hover:border-primary/40 hover:bg-muted/20 transition-colors cursor-pointer"
              onClick={() => setViewingVersion(v)}
            >
              <div className="flex items-start gap-2">
                <Tag className="text-[10px] shrink-0 mt-0.5">
                  v{v.version}
                </Tag>
                <div className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                  <VersionLabel version={v} onSave={handleSaveLabel} />
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0">{formatDateTime(v.createdAt)}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      <div
        className="group border rounded-lg p-3 border-dashed hover:border-primary/40 hover:bg-muted/10 transition-colors cursor-pointer"
        onClick={() => setViewingVersion(null)}
      >
        <div className="flex items-center gap-2">
          <Tag color="processing" className="text-[10px] shrink-0">live</Tag>
          <span className="text-xs font-medium">Current working roadmap</span>
        </div>
      </div>
    </div>
  );
}
